// FILE: client/src/screens/admin/AdminDashboardScreen.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Badge,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Loader from "../../components/Loader";
import Message from "../../components/Message";
import { SHOPS } from "../../constants/shopMeta";
import { getApiErrorMessage } from "../../utils/errorMessages";

import { listAdminProducts } from "../../actions/adminProductActions";
import { adminListCategories } from "../../actions/categoryActions";
import { adminListSubcategories } from "../../actions/subcategoryActions";
import { getAdminOrderAnalytics } from "../../actions/adminOrderActions";
import { listCreditsAdmin } from "../../actions/creditActions";
import { listPromoCodesAdmin } from "../../actions/promoCodeActions";
import { listContactMessages } from "../../actions/contactActions";
import { getAboutAdmin } from "../../actions/aboutActions";
import { listPortfolioAdmin } from "../../actions/portfolioActions";
import { listJewelryHireItemsAdmin } from "../../actions/jewelryHireActions";
import { getChatUnreadAdmin } from "../../actions/chatActions";
import {
  listServiceBookingsAdmin,
  listServiceOrdersAdmin,
  listServicesAdmin,
} from "../../actions/serviceActions";
import { getAdminMadeToMeasureEnquiries } from "../../actions/madeToMeasureActions";
import { listNewsletterSubscribersAdmin } from "../../actions/newsletterActions";

const EMPTY_STATS = {
  products: null,
  categories: null,
  subcategories: null,
  jewelryHire: null,
  portfolio: null,

  orders: null,
  paidOrders: null,
  grossRevenue: null,
  refunds: null,
  netRevenue: null,
  openReturns: null,
  messages: null,
  unreadMessages: null,

  credits: null,
  promoCodes: null,
  newsletterSubscribers: null,
  activeNewsletterSubscribers: null,
  contacts: null,
  newContacts: null,
  services: null,
  serviceBookings: null,
  newServiceBookings: null,
  serviceOrders: null,
  madeToMeasure: null,
  openMadeToMeasure: null,
  about: null,
};

function getErrorMessage(
  error,
  fallback = "We could not load dashboard data right now. Please try again later.",
) {
  if (!error) return fallback;

  if (typeof error === "string") return error;

  return getApiErrorMessage(error, {
    fallback: error?.payload?.message || fallback,
  });
}

function formatMoney(value = 0, currency = "GBP") {
  const amount = Number(value || 0);
  const safeAmount = Number.isFinite(amount) ? amount : 0;

  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
    }).format(safeAmount);
  } catch {
    return `£${safeAmount.toFixed(2)}`;
  }
}

function toCount(payload, collectionKeys = []) {
  if (typeof payload?.count === "number") return payload.count;
  if (typeof payload?.total === "number") return payload.total;
  if (typeof payload?.totalOrders === "number") return payload.totalOrders;

  if (typeof payload?.pagination?.count === "number") {
    return payload.pagination.count;
  }

  if (typeof payload?.pagination?.total === "number") {
    return payload.pagination.total;
  }

  for (const key of collectionKeys) {
    if (Array.isArray(payload?.[key])) return payload[key].length;
  }

  if (Array.isArray(payload)) return payload.length;

  return 0;
}

function toNumber(value, fallback = 0) {
  const number = Number(value);

  return Number.isFinite(number) ? number : fallback;
}

function toArray(payload, collectionKeys = []) {
  if (Array.isArray(payload)) return payload;

  for (const key of collectionKeys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }

  return [];
}

async function settleDashboardTask(key, task) {
  try {
    const value = await task();

    return {
      key,
      ok: true,
      value,
      error: "",
    };
  } catch (error) {
    return {
      key,
      ok: false,
      value: null,
      error: getErrorMessage(error),
    };
  }
}

function StatCard({
  title,
  value,
  subtitle,
  to,
  variant = "dark",
  badgeText = "",
}) {
  return (
    <Card className="shadow-sm h-100">
      <Card.Body className="d-flex flex-column">
        <div className="d-flex justify-content-between align-items-start gap-2">
          <div
            className="text-muted"
            style={{
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              fontWeight: 700,
            }}
          >
            {title}
          </div>

          {badgeText ? <Badge bg="danger">{badgeText}</Badge> : null}
        </div>

        <div style={{ fontSize: "2rem", fontWeight: 800, marginTop: 8 }}>
          {value}
        </div>

        {subtitle && <div className="text-muted mt-1">{subtitle}</div>}

        <div className="mt-auto pt-3">
          <Link to={to}>
            <Button variant={variant}>Open</Button>
          </Link>
        </div>
      </Card.Body>
    </Card>
  );
}

export default function AdminDashboardScreen() {
  const dispatch = useDispatch();
  const activeRequestRef = useRef(0);
  const hasRequestedGlobalCatalogStatsRef = useRef(false);

  const { userInfo = null } = useSelector((state) => state.userAuth || {});

  const getRoles = (user) => {
    const value = user?.roles ?? user?.role;
    if (Array.isArray(value)) {
      return value.map((role) => String(role).trim().toLowerCase()).filter(Boolean);
    }
    return String(value || "").split(",").map((role) => role.trim().toLowerCase()).filter(Boolean);
  };

  const userRoles = getRoles(userInfo);
  const isStoreManager = userRoles.includes("store_manager");
  const isSuperAdmin = userRoles.includes("super_admin");
  const isLegacyAdmin = userRoles.includes("admin");
  const isRestrictedManager = isStoreManager && !isSuperAdmin && !isLegacyAdmin;

  const defaultShopKey = SHOPS[0]?.shopKey || "haberdashery";

  const [loading, setLoading] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [shopKey, setShopKey] = useState(defaultShopKey);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [lastUpdated, setLastUpdated] = useState(null);

  const currentShop = useMemo(
    () => SHOPS.find((shop) => shop.shopKey === shopKey) || null,
    [shopKey],
  );

  const fetchStats = useCallback(async ({ refreshGlobal = false } = {}) => {
    const requestId = activeRequestRef.current + 1;
    activeRequestRef.current = requestId;

    setLoading(true);
    setWarnings([]);

    const includeGlobalCatalogStats =
      !isRestrictedManager &&
      (refreshGlobal || !hasRequestedGlobalCatalogStatsRef.current);

    if (includeGlobalCatalogStats) {
      hasRequestedGlobalCatalogStatsRef.current = true;
    }

    const tasks = [
      [
        "products",
        () =>
          dispatch(
            listAdminProducts({
              shopKey,
              page: 1,
              limit: 1,
            }),
          ),
      ],
      [
        "categories",
        () =>
          dispatch(
            adminListCategories({
              shopKey,
              pageNumber: 1,
              pageSize: 1,
            }),
          ),
      ],
      [
        "subcategories",
        () =>
          dispatch(
            adminListSubcategories({
              shopKey,
              pageNumber: 1,
              pageSize: 1,
            }),
          ),
      ],
      ["orders", () => dispatch(getAdminOrderAnalytics())],
      ["messages", () => dispatch(getChatUnreadAdmin())],
      ["serviceOrders", () => dispatch(listServiceOrdersAdmin("all"))],
      ["madeToMeasure", () => dispatch(getAdminMadeToMeasureEnquiries())],
      [
        "jewelryHire",
        () =>
          dispatch(
            listJewelryHireItemsAdmin({ pageNumber: 1, pageSize: 1 }),
          ),
      ],
    ];

    if (!isRestrictedManager) {
      tasks.push(
        ["contacts", () => dispatch(listContactMessages())],
        ["serviceBookings", () => dispatch(listServiceBookingsAdmin("all"))],
        ["about", () => dispatch(getAboutAdmin())],
        ["credits", () => dispatch(listCreditsAdmin())],
        ["promoCodes", () => dispatch(listPromoCodesAdmin({ limit: 1 }))],
        [
          "newsletter",
          () =>
            dispatch(
              listNewsletterSubscribersAdmin({
                status: "all",
                page: 1,
                limit: 1,
              }),
            ),
        ],
      );

      if (includeGlobalCatalogStats) {
        tasks.push(
          ["services", () => dispatch(listServicesAdmin())],
          ["portfolio", () => dispatch(listPortfolioAdmin("production"))],
        );
      }
    }

    const results = await Promise.all(
      tasks.map(([key, task]) => settleDashboardTask(key, task)),
    );

    if (activeRequestRef.current !== requestId) return;

    const byKey = Object.fromEntries(
      results.map((result) => [result.key, result]),
    );

    const contactsArray = toArray(byKey.contacts?.value, ["contacts"]);
    const serviceBookingsArray = toArray(byKey.serviceBookings?.value, [
      "bookings",
    ]);
    const serviceOrdersArray = toArray(byKey.serviceOrders?.value, [
      "serviceOrders",
      "orders",
    ]);
    const madeToMeasureArray = toArray(byKey.madeToMeasure?.value, [
      "enquiries",
    ]);
    const orderAnalytics = byKey.orders?.value || {};

    const nextStats = {
      products: byKey.products?.ok
        ? toCount(byKey.products.value, ["products"])
        : null,

      categories: byKey.categories?.ok
        ? toCount(byKey.categories.value, ["categories"])
        : null,

      subcategories: byKey.subcategories?.ok
        ? toCount(byKey.subcategories.value, ["subcategories"])
        : null,

      jewelryHire: byKey.jewelryHire?.ok
        ? toCount(byKey.jewelryHire.value, ["items"])
        : null,

      orders: byKey.orders?.ok ? toCount(orderAnalytics, ["orders"]) : null,

      paidOrders: byKey.orders?.ok
        ? toNumber(orderAnalytics.paidOrderCount, 0)
        : null,

      grossRevenue: byKey.orders?.ok
        ? toNumber(orderAnalytics.grossRevenue, 0)
        : null,

      refunds: byKey.orders?.ok ? toNumber(orderAnalytics.refunds, 0) : null,

      netRevenue: byKey.orders?.ok
        ? toNumber(orderAnalytics.netRevenue, 0)
        : null,

      openReturns: byKey.orders?.ok
        ? toNumber(orderAnalytics.openReturns, 0)
        : null,

      messages: byKey.messages?.ok
        ? toNumber(byKey.messages.value?.unread?.conversations, 0)
        : null,

      unreadMessages: byKey.messages?.ok
        ? toNumber(byKey.messages.value?.unread?.messages, 0)
        : null,

      credits: byKey.credits?.ok
        ? toCount(byKey.credits.value, ["credits"])
        : null,

      promoCodes: byKey.promoCodes?.ok
        ? toCount(byKey.promoCodes.value, ["promoCodes"])
        : null,

      newsletterSubscribers: byKey.newsletter?.ok
        ? toNumber(byKey.newsletter.value?.counts?.total, 0)
        : null,

      activeNewsletterSubscribers: byKey.newsletter?.ok
        ? toNumber(byKey.newsletter.value?.counts?.active, 0)
        : null,

      contacts: byKey.contacts?.ok ? contactsArray.length : null,

      newContacts: byKey.contacts?.ok
        ? contactsArray.filter(
            (contact) =>
              String(contact?.status || "")
                .trim()
                .toLowerCase() === "new",
          ).length
        : null,

      serviceBookings: byKey.serviceBookings?.ok
        ? serviceBookingsArray.length
        : null,

      newServiceBookings: byKey.serviceBookings?.ok
        ? serviceBookingsArray.filter(
            (booking) =>
              String(booking?.status || "")
                .trim()
                .toLowerCase() === "new",
          ).length
        : null,

      serviceOrders: byKey.serviceOrders?.ok ? serviceOrdersArray.length : null,

      madeToMeasure: byKey.madeToMeasure?.ok ? madeToMeasureArray.length : null,

      openMadeToMeasure: byKey.madeToMeasure?.ok
        ? madeToMeasureArray.filter(
            (enquiry) =>
              !["paid", "completed", "rejected"].includes(
                String(enquiry?.status || "")
                  .trim()
                  .toLowerCase(),
              ),
          ).length
        : null,

      about: byKey.about?.ok
        ? byKey.about.value?.exists === true ||
          Boolean(byKey.about.value?._id) ||
          Boolean(byKey.about.value?.title)
          ? 1
          : 0
        : null,
    };

    if (includeGlobalCatalogStats) {
      nextStats.portfolio = byKey.portfolio?.ok
        ? toCount(byKey.portfolio.value, ["items", "portfolio", "slides"])
        : null;

      nextStats.services = byKey.services?.ok
        ? toCount(byKey.services.value, ["services"])
        : null;
    }

    const failedWarnings = results
      .filter((result) => !result.ok)
      .map((result) => `${result.key}: ${result.error}`);

    setStats((currentStats) => ({
      ...currentStats,
      ...nextStats,
    }));
    setWarnings(failedWarnings);
    setLastUpdated(new Date());
    setLoading(false);
  }, [dispatch, shopKey, isRestrictedManager]);

  useEffect(() => {
    fetchStats();

    return () => {
      activeRequestRef.current += 1;
    };
  }, [fetchStats]);

  return (
    <Container className="py-4">
      <Row className="align-items-end g-3 mb-3">
        <Col>
          <h1>Admin Dashboard</h1>

          <div className="text-muted">
            Studio admin shortcuts
            {currentShop ? ` — ${currentShop.name}` : ""}.
          </div>

          {lastUpdated ? (
            <div className="text-muted small mt-1">
              Last refreshed: {lastUpdated.toLocaleString()}
            </div>
          ) : null}
        </Col>

        <Col md="auto">
          <Form.Select
            value={shopKey}
            onChange={(e) => setShopKey(e.target.value)}
            style={{ minWidth: 240 }}
            aria-label="Select shop"
          >
            {SHOPS.map((shop) => (
              <option key={shop.shopKey} value={shop.shopKey}>
                {shop.name}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md="auto">
          <Button
            variant="outline-dark"
            onClick={() => fetchStats({ refreshGlobal: true })}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </Col>
      </Row>

      {loading && <Loader />}

      {warnings.length > 0 ? (
        <Message variant="warning">
          Some dashboard stats could not load: {warnings.join(" | ")}
        </Message>
      ) : null}

      <Row className="g-3">
        <Col md={6} lg={4}>
          <StatCard
            title="Products"
            value={stats.products ?? "—"}
            subtitle={`For: ${currentShop?.name || shopKey}`}
            to="/admin/products"
          />
        </Col>

        <Col md={6} lg={4}>
          <StatCard
            title="Categories"
            value={stats.categories ?? "—"}
            subtitle={`For: ${currentShop?.name || shopKey}`}
            to={`/admin/shop/${shopKey}/categories`}
            variant="outline-dark"
          />
        </Col>

        <Col md={6} lg={4}>
          <StatCard
            title="Subcategories"
            value={stats.subcategories ?? "—"}
            subtitle={`For: ${currentShop?.name || shopKey}`}
            to={`/admin/shop/${shopKey}/subcategories`}
            variant="outline-dark"
          />
        </Col>

        <Col md={6} lg={4}>
          <StatCard
            title="Jewellery Hire"
            value={stats.jewelryHire ?? "—"}
            subtitle="Catalogue items"
            to="/admin/jewelry-hire"
            variant="outline-dark"
          />
        </Col>

        {!isRestrictedManager && (
          <Col md={6} lg={4}>
            <StatCard
              title="Portfolio Slides"
              value={stats.portfolio ?? "—"}
              subtitle="Production/Bespoke"
              to="/admin/portfolio"
            />
          </Col>
        )}

        <Col md={6} lg={4}>
          <StatCard
            title="Orders"
            value={stats.orders ?? "—"}
            subtitle={
              stats.paidOrders === null
                ? "All shops"
                : `${stats.paidOrders} paid orders`
            }
            to="/admin/orders"
          />
        </Col>

        <Col md={6} lg={4}>
          <StatCard
            title="Net Revenue"
            value={
              stats.netRevenue === null
                ? "—"
                : formatMoney(stats.netRevenue, "GBP")
            }
            subtitle={
              stats.refunds === null
                ? "After refunds"
                : `${formatMoney(stats.refunds, "GBP")} refunded`
            }
            to="/admin/orders"
            variant="outline-dark"
          />
        </Col>

        <Col md={6} lg={4}>
          <StatCard
            title="Open Returns"
            value={stats.openReturns ?? "—"}
            subtitle="Pending return workflow"
            to="/admin/orders/returns"
            variant="outline-dark"
            badgeText={
              stats.openReturns && stats.openReturns > 0
                ? `${stats.openReturns} open`
                : ""
            }
          />
        </Col>

        <Col md={6} lg={4}>
          <StatCard
            title="Messages"
            value={stats.messages ?? "—"}
            subtitle={
              stats.unreadMessages === null
                ? "Customer chats"
                : `${stats.unreadMessages} unread messages`
            }
            to="/admin/messages"
            variant="outline-dark"
            badgeText={
              stats.unreadMessages && stats.unreadMessages > 0
                ? `${stats.unreadMessages} unread`
                : ""
            }
          />
        </Col>

        {!isRestrictedManager && (
          <Col md={6} lg={4}>
            <StatCard
              title="Credits"
              value={stats.credits ?? "—"}
              subtitle="Blocks on /credits"
              to="/admin/credits"
              variant="outline-dark"
            />
          </Col>
        )}

        {!isRestrictedManager && (
          <Col md={6} lg={4}>
            <StatCard
              title="Promo Codes"
              value={stats.promoCodes ?? "—"}
              subtitle="Checkout discounts"
              to="/admin/promo-codes"
              variant="outline-dark"
            />
          </Col>
        )}

        {!isRestrictedManager && (
          <Col md={6} lg={4}>
            <StatCard
              title="Newsletter Subscribers"
              value={stats.newsletterSubscribers ?? "—"}
              subtitle={
                stats.activeNewsletterSubscribers === null
                  ? "Marketing list"
                  : `${stats.activeNewsletterSubscribers} active`
              }
              to="/admin/newsletter"
              variant="outline-dark"
            />
          </Col>
        )}

        {!isRestrictedManager && (
          <Col md={6} lg={4}>
            <StatCard
              title="Contacts"
              value={stats.contacts ?? "—"}
              subtitle="Contact submissions"
              to="/admin/contact/messages"
              variant="outline-dark"
              badgeText={
                stats.newContacts && stats.newContacts > 0
                  ? `${stats.newContacts} new`
                  : ""
              }
            />
          </Col>
        )}

        {!isRestrictedManager && (
          <Col md={6} lg={4}>
            <StatCard
              title="Services"
              value={stats.services ?? "—"}
              subtitle="In-house service catalog"
              to="/admin/services"
              variant="outline-dark"
            />
          </Col>
        )}

        {!isRestrictedManager && (
          <Col md={6} lg={4}>
            <StatCard
              title="Service Reservations"
              value={stats.serviceBookings ?? "—"}
              subtitle="Appointment requests"
              to="/admin/services/bookings"
              variant="outline-dark"
              badgeText={
                stats.newServiceBookings && stats.newServiceBookings > 0
                  ? `${stats.newServiceBookings} new`
                  : ""
              }
            />
          </Col>
        )}

        <Col md={6} lg={4}>
          <StatCard
            title="Service Orders"
            value={stats.serviceOrders ?? "—"}
            subtitle="Paid service workflow"
            to="/admin/services/orders"
            variant="outline-dark"
          />
        </Col>

        <Col md={6} lg={4}>
          <StatCard
            title="Made To Measure"
            value={stats.madeToMeasure ?? "—"}
            subtitle="Custom quote enquiries"
            to="/admin/made-to-measure"
            variant="outline-dark"
            badgeText={
              stats.openMadeToMeasure && stats.openMadeToMeasure > 0
                ? `${stats.openMadeToMeasure} open`
                : ""
            }
          />
        </Col>

        {!isRestrictedManager && (
          <Col md={6} lg={4}>
            <StatCard
              title="About Page"
              value={stats.about ?? "—"}
              subtitle="Site content"
              to="/admin/about"
              variant="outline-dark"
            />
          </Col>
        )}

        <Col lg={12}>
          <Card className="shadow-sm">
            <Card.Body className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
              <div>
                <div
                  className="text-muted"
                  style={{
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 700,
                  }}
                >
                  Quick Links
                </div>

                <div className="text-muted mt-1">
                  Jump to the tools you use most.
                </div>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <Link to="/admin/products">
                  <Button variant="dark">Products</Button>
                </Link>

                <Link to={`/admin/shop/${shopKey}/categories`}>
                  <Button variant="outline-dark">Categories</Button>
                </Link>

                <Link to={`/admin/shop/${shopKey}/subcategories`}>
                  <Button variant="outline-dark">Subcategories</Button>
                </Link>

                {!isRestrictedManager && (
                  <Link to="/admin/users">
                    <Button variant="outline-dark">Users</Button>
                  </Link>
                )}

                {!isRestrictedManager && (
                  <Link to="/admin/portfolio">
                    <Button variant="outline-dark">Portfolio</Button>
                  </Link>
                )}

                <Link to="/admin/orders">
                  <Button variant="outline-dark">Orders</Button>
                </Link>

                <Link to="/admin/orders/returns">
                  <Button variant="outline-dark">Returns</Button>
                </Link>

                <Link to="/admin/messages">
                  <Button variant="outline-dark">Messages</Button>
                </Link>

                {!isRestrictedManager && (
                  <Link to="/admin/credits">
                    <Button variant="outline-dark">Credits</Button>
                  </Link>
                )}

                {!isRestrictedManager && (
                  <Link to="/admin/contact/messages">
                    <Button variant="outline-dark">Contacts</Button>
                  </Link>
                )}

                {!isRestrictedManager && (
                  <Link to="/admin/newsletter">
                    <Button variant="outline-dark">Newsletter</Button>
                  </Link>
                )}

                {!isRestrictedManager && (
                  <Link to="/admin/contact/content">
                    <Button variant="outline-dark">Contact Content</Button>
                  </Link>
                )}

                {!isRestrictedManager && (
                  <Link to="/admin/services">
                    <Button variant="outline-dark">Services</Button>
                  </Link>
                )}

                <Link to="/admin/services/orders">
                  <Button variant="outline-dark">Service Orders</Button>
                </Link>

                {!isRestrictedManager && (
                  <Link to="/admin/services/bookings">
                    <Button variant="outline-dark">Service Reservations</Button>
                  </Link>
                )}

                <Link to="/admin/made-to-measure">
                  <Button variant="outline-dark">Made To Measure</Button>
                </Link>

                {!isRestrictedManager && (
                  <Link to="/admin/about">
                    <Button variant="outline-dark">About</Button>
                  </Link>
                )}

                {!isRestrictedManager && (
                  <Link to="/admin/legal">
                    <Button variant="outline-dark">Legal Pages</Button>
                  </Link>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
